import React, { useEffect } from "react";
import { makeStyles } from "@mui/styles";
import { useDispatch, useSelector } from "react-redux";
import { Button, TableCell, TablePagination, TextField } from "@mui/material";
import CustomTable from "../../components/CustomTable";
import CONSTANTS, {
  MODAL_ACTIONS,
  PRODUCT_CATEGORY_FORM_ID,
  PRODUCT_CODES_FORM_ID,
  USER_GROUPS,
} from "../../constants/constants";
import { pushNotification } from "../../redux/actions";
import {
  DeleteRuntimeFormData,
  fetchRuntimeFormData,
} from "../../services/FormService";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Typography from "@mui/material/Typography";
import CreateProductCategory from "./CreateProductCategory";
import Colors from "../../theme/colors.json";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

const useStyles = makeStyles((theme) => ({
  categoryRegisterContainer: {
    width: "100%",
    gap: 20,
  },
  categoryRegisterHeadingSection: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-end",
  },
  HeadingSectionText: {
    fontSize: 22,
    fontWeight: 700,
    lineHeight: "15px",
    color: theme.palette.secondary.main,
  },
  categoryRegisterSearchContainer: {
    display: "flex",
    flexDirection: "row",
    gap: 10,
  },
  categoryRegisterTableSection: {
    marginTop: 20,
  },
  categoryRegisterPaginationSection: {},
  tablePagination: {},
  deleteformContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    gap: 30,
  },
  deleteformBtnContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    gap: 10,
  },
  deleteformContainerText: {
    fontSize: theme.fontSizes.LARGE,
    fontWeight: 700,
    lineHeight: "13px",
    color: theme.palette.primary.main,
  },
  searchField: {
    background: Colors.WHITE_BACKGROUND,
    paddingLeft: "0.4rem",
    borderRadius: "0.3rem",
  },
  commonBtnStyles: {
    background: Colors.WHITE_BACKGROUND,
    textTransform: "capitalize",
  },
}));

const RegisterProductCategory = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { userGroup } = useSelector((state: any) => ({
    userGroup: state.userGroup,
  }));

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [totalRecords, setTotalRecords] = React.useState(0);
  const [tableData, setTableData] = React.useState<any>([]);
  const [modalTitle, setModalTitle] = React.useState<any>("");
  const [selectedRow, setSelectedRow] = React.useState<any>({});
  const [refresh, setRefresh] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [backupRowData, setBackupRowData] = React.useState<any>([]);

  const [open, setOpen] = React.useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
    setModalTitle("");
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const Columns: any = [
    {
      id: "productCategory",
      label: "Product Category",
      align: "center",
      minWidth: 120,
    },
  ];

  const handleEdit = async (e) => {
    await setSelectedRow(e);
    await setModalTitle(MODAL_ACTIONS.EDIT);
    await handleClickOpen();
  };

  const handleDelete = (e) => {
    setModalTitle(MODAL_ACTIONS.DELETE);
    setSelectedRow(e);
    handleClickOpen();
  };

  const handleCreateNew = () => {
    setSelectedRow({});
    setModalTitle(MODAL_ACTIONS.CREATE);
    handleClickOpen();
  };

  const handleGetAllProductCategories = async () => {
    const response = await fetchRuntimeFormData(
      PRODUCT_CATEGORY_FORM_ID,
      page,
      rowsPerPage
    );

    if (response?.success) {
      const filteredRows = response?.data?.content?.map((item) => {
        return {
          ...item?.formData,
          id: item?.id,
        };
      });

      setTableData(filteredRows);
      setBackupRowData(filteredRows);
      setTotalRecords(response?.data?.totalElements);
    } else {
      dispatch(
        pushNotification({
          isOpen: true,
          message: response.message,
          type: CONSTANTS.ERROR,
        })
      );
      setTotalRecords(0);
      setTableData([]);
      setBackupRowData([]);
    }
  };

  const handleWildcardSearch = (inputString, dataArray) => {
    const searchResults: any = [];

    // Loop through the data array
    for (const data of dataArray) {
      const escapedInput = inputString.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); // Escape special regex characters
      // Convert the object values to a single string
      const dataString = Object.values(data).join(" ");

      // Perform wildcard search using regular expression
      const regex = new RegExp(escapedInput.replace(/\*/g, ".*"), "i");
      if (dataString.match(regex)) {
        searchResults.push(data);
      }
    }
    setTableData(searchResults);
  };

  const handleModalRender = (action) => {
    switch (action) {
      case MODAL_ACTIONS.CREATE:
        return (
          <CreateProductCategory
            data={selectedRow}
            setOpen={setOpen}
            setRefresh={setRefresh}
          />
        );
      case MODAL_ACTIONS.EDIT:
        return (
          <CreateProductCategory
            data={selectedRow}
            setOpen={setOpen}
            setRefresh={setRefresh}
          />
        );
      case MODAL_ACTIONS.DELETE:
        return <DeleteProductCategory />;

      default:
        return null;
    }
  };

  useEffect(() => {
    if (searchTerm === "" || null || undefined) {
      setTableData(backupRowData);
    } else {
      handleWildcardSearch(searchTerm, backupRowData);
    }
  }, [searchTerm]);

  useEffect(() => {
    handleGetAllProductCategories();
  }, [page, rowsPerPage, refresh]);

  const DeleteProductCategory = () => {
    const handleProductCategoryDeletion = async (selectedRow) => {
      const response = await DeleteRuntimeFormData(
        PRODUCT_CATEGORY_FORM_ID,
        selectedRow.id
      );
      if (response?.success) {
        handleClose();
        setRefresh((prevState) => !prevState);
        dispatch(
          pushNotification({
            isOpen: true,
            message: response.message,
            type: CONSTANTS.SUCCESS,
          })
        );
      } else {
        handleClose();
        setRefresh((prevState) => !prevState);
        dispatch(
          pushNotification({
            isOpen: true,
            message: response.message,
            type: CONSTANTS.ERROR,
          })
        );
      }
    };

    return (
      <div className={classes.deleteformContainer}>
        <div>
          Are you sure you want to delete{" "}
          {
            <span className={classes.deleteformContainerText}>
              {selectedRow.productCategory}
            </span>
          }{" "}
          category
        </div>
        <div className={classes.deleteformBtnContainer}>
          <Button
            size="small"
            color="primary"
            variant="contained"
            onClick={() => handleProductCategoryDeletion(selectedRow)}
          >
            Delete
          </Button>
          <Button
            size="small"
            color="secondary"
            variant="contained"
            onClick={handleClose}
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className={classes.categoryRegisterContainer}>
      <BootstrapDialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
      >
        <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
          {modalTitle} Product Category
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent dividers>{handleModalRender(modalTitle)}</DialogContent>
      </BootstrapDialog>
      <div className={classes.categoryRegisterHeadingSection}>
        {/* <label className={classes.HeadingSectionText}>Product Categories</label> */}
        <div className={classes.categoryRegisterSearchContainer}>
          <TextField
            variant="standard"
            placeholder="Search"
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              disableUnderline: true,
              classes: {
                input: classes.searchField,
              },
            }}
          />
          <Button
            size="small"
            variant="outlined"
            className={classes.commonBtnStyles}
            onClick={handleCreateNew}
          >
            Create New
          </Button>
        </div>
      </div>
      <div className={classes.categoryRegisterTableSection}>
        <CustomTable
          columns={Columns}
          data={tableData}
          handleEdit={handleEdit}
          handleDelete={handleDelete}
          isEdit={true}
          isDelete={userGroup === USER_GROUPS.ADMIN ? true : false}
        />
      </div>
      <div className={classes.categoryRegisterPaginationSection}>
        <TablePagination
          component="div"
          className={classes.tablePagination}
          count={totalRecords}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          showFirstButton
          showLastButton
        />
      </div>
    </div>
  );
};

export default RegisterProductCategory;
